import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getSavedSearches, createSavedSearch, updateSavedSearch, deleteSavedSearch } from '../api/savedSearches'
import { SavedSearchScope } from '../types/savedSearch'

export const savedSearchesKeys = {
  all: ['savedSearches'] as const,
  byScope: (scope: SavedSearchScope) => ['savedSearches', scope] as const,
}

export function useSavedSearches<F = unknown>(scope: SavedSearchScope = 'releases') {
  return useQuery({
    queryKey: savedSearchesKeys.byScope(scope),
    queryFn: () => getSavedSearches<F>(scope),
    staleTime: 1000 * 60 * 5,
  })
}

export function useCreateSavedSearch<F = unknown>(scope: SavedSearchScope = 'releases') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, filters }: { name: string; filters: F }) =>
      createSavedSearch<F>(name, filters, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchesKeys.byScope(scope) })
    },
  })
}

export function useUpdateSavedSearch<F = unknown>() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: { name?: string; filters?: F } }) =>
      updateSavedSearch<F>(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchesKeys.all })
    },
  })
}

export function useDeleteSavedSearch(scope: SavedSearchScope = 'releases') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteSavedSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchesKeys.byScope(scope) })
    },
  })
}
